// What the top bar says, and what its buttons do.
//
// The design puts the screen's title, its row count and its export action in
// the SHELL rather than in the page — so the shell has to learn them from
// whichever page is mounted. A page declares them; the shell renders whatever
// was last declared.
//
// There WAS a one-line subtitle beside the title. It is gone: the global search
// box sits in the middle of that bar now, and a sentence of prose beside the
// title crowded it.
//
// There is a static fallback keyed by pathname as well, so a screen that never
// declares anything still gets a correct title rather than a blank bar. Pages
// only need `usePageChrome` when the title is dynamic (a customer's name) or
// when they have something to export.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export interface PageChrome {
  title: string;
  /**
   * One line COUNTED from what the screen loaded, beside the screen's title.
   * A page declares it only once its rows are read,
   * so the bar never states a total nobody has yet.
   */
  summary?: string;
  /** Present only on screens with a table worth exporting. */
  onExport?: () => void;
  /** What the export produces, for the button's tooltip. */
  exportLabel?: string;
}

interface ChromeStore {
  chrome: PageChrome | null;
  declare: (chrome: PageChrome | null) => void;
}

const PageChromeContext = createContext<ChromeStore | null>(null);

export function PageChromeProvider({ children }: { children: ReactNode }): ReactNode {
  const [chrome, setChrome] = useState<PageChrome | null>(null);
  const store = useMemo<ChromeStore>(() => ({ chrome, declare: setChrome }), [chrome]);
  return <PageChromeContext.Provider value={store}>{children}</PageChromeContext.Provider>;
}

/**
 * Declare this screen's chrome. Serialised into the dependency list rather than
 * passed by reference: a caller building `{ title, summary }` inline creates a
 * new object every render, and depending on the object identity would re-set
 * state forever.
 */
export function usePageChrome(chrome: PageChrome): void {
  const store = useContext(PageChromeContext);
  const { title, summary, onExport, exportLabel } = chrome;

  useEffect(() => {
    if (store === null) return;
    store.declare({
      title,
      ...(summary === undefined ? {} : { summary }),
      ...(onExport === undefined ? {} : { onExport }),
      ...(exportLabel === undefined ? {} : { exportLabel }),
    });
    // Clearing on unmount is what stops a stale title surviving a navigation
    // to a screen that declares nothing.
    return () => store.declare(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- store identity changes every declare
  }, [title, summary, onExport, exportLabel]);
}

export function useDeclaredChrome(): PageChrome | null {
  return useContext(PageChromeContext)?.chrome ?? null;
}

/**
 * The fallback title for every static screen, longest prefix first so
 * `/deals/abc` matches "Deal" rather than "Deals".
 */
const FALLBACK_CHROME: ReadonlyArray<readonly [string, PageChrome]> = [
  ["/deals/", { title: "Deal" }],
  ["/usage", { title: "Platform costs" }],
  ["/deals", { title: "Deals" }],
  ["/billing", { title: "To bill" }],
  ["/calendar", { title: "Calendar" }],
  ["/partners/", { title: "Partner" }],
  ["/partners", { title: "Partners" }],
  ["/map", { title: "Map" }],
  ["/regions", { title: "Regions" }],
  ["/margin", { title: "Margin" }],
  ["/projections", { title: "Projections" }],
  ["/sales", { title: "Sales performance" }],
  ["/pricing", { title: "Pricing calculator" }],
  ["/fx-rates", { title: "FX rates" }],
  ["/rates", { title: "Rate card" }],
  ["/", { title: "Dashboard" }],
];

export function fallbackChrome(pathname: string): PageChrome {
  for (const [prefix, chrome] of FALLBACK_CHROME) {
    if (pathname.startsWith(prefix)) return chrome;
  }
  return { title: "Dial AI CRM" };
}
